import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { TAX_TYPES } from "src/constants/taxTypes"
import { useMarina } from "src/contexts/MarinaContext"
import NumericInput from "src/main/PointOfSale/CurrentSalePanel/NumericInput"

import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { createMarinaTax } from "src/api/Manage/Taxes"

import { useToast } from "src/hooks/use_toast"

export default function AddTaxModal({ isOpen, onClose }) {
  const { marinaSlug } = useMarina()
  const showToast = useToast()

  const {
    register,
    reset,
    resetField,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      taxType: "",
      rate: 0.01,
    },
  })

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      reset()
      resetField("rate")
    }, 300)
  }

  const { mutate: createTaxMutation, isLoading: createTaxLoading } =
    useMutation({
      mutationKey: "createTax",
      mutationFn: async (data) => createMarinaTax(marinaSlug, data),
      onSuccess: ({ data: { taxTypeDisplay, name } }) => {
        showToast(`${taxTypeDisplay} "${name}" has been added`, {
          type: "success",
        })
        handleClose()
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    })

  const handleFormSubmit = (data) => {
    createTaxMutation(data)
  }

  return (
    <Modal onClose={handleClose} isOpen={isOpen} size="mediumFixed">
      <Modal.Header title={"Add Tax"} />
      <Modal.Body>
        <Form>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <section>
              <Form.Label htmlFor="name" required>
                Name
              </Form.Label>
              <Form.TextField {...register("name", { required: true })} />
              <Form.Error>{errors.name && "Name is required"}</Form.Error>
            </section>
            <section>
              <Form.Label htmlFor="taxType" required>
                Tax Type
              </Form.Label>
              <Form.Select {...register("taxType", { required: true })}>
                <option value="" disabled>
                  Select Tax Type
                </option>
                {TAX_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Error>
                {errors.taxType && "Tax Type is required"}
              </Form.Error>
            </section>
            <section>
              <Form.Label htmlFor="rate" required>
                Rate
              </Form.Label>
              <Controller
                name="rate"
                control={control}
                rules={{
                  required: "Tax is required",
                  validate: {
                    greaterThanZero: (value) =>
                      value > 0 || "Must be greater than 0",
                    decimals: (value) => {
                      const [, decimals] = String(value).split(".")

                      if (decimals?.length > 8) {
                        return "Tax can only be specified up to 6 decimal places"
                      }
                    },
                  },
                  max: { value: 100, message: "Tax cannot be more than 100%" },
                }}
                render={({ field: { onChange, value } }) => {
                  const displayValue = new Decimal(value).mul(100).toNumber()
                  return (
                    <NumericInput
                      id={"rate"}
                      value={displayValue}
                      onChange={(updatedValue) => {
                        const value = new Decimal(updatedValue)
                          .div(100)
                          .toNumber()
                        onChange(value)
                      }}
                      precision={4}
                      icon="%"
                      minValue={0.0001}
                      maxValue={100}
                      disabled={false}
                      hasErrors={false}
                    />
                  )
                }}
              />
              <Form.Error>{errors.rate && errors.rate.message}</Form.Error>
            </section>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer
        confirmBtnType={"submit"}
        onSubmit={handleSubmit(handleFormSubmit)}
        onClose={handleClose}
        confirmBtnLoading={createTaxLoading}
      />
    </Modal>
  )
}

AddTaxModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}
